import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Link,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import PropTypes from "prop-types";
import { Check, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import PageHeader from "../PageHeader";
import UserAvatar from "../people/UserAvatar";
import TaskModal from "../tasks/TaskModal";
import useHouseholdUsers from "../../hooks/useHouseholdUsers";
import { discussionsChanged } from "../../hooks/useForYouCount";
import usePoll from "../../hooks/usePoll";
import useProjects from "../../hooks/useProjects";
import { discussionApi, taskApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import {
  displayName,
  isBlankName,
  relativeTime,
} from "../tasks/userDisplay";
import {
  panelSummary,
  taskDescriptionFrom,
  taskTitleFrom,
  withMention,
} from "./discussionText";

// Posts are markdown from people AND agents: the same sanitized renderer
// as snippets (no remote images or embeds — viewing fires no outside
// requests), loaded on demand.
const MarkdownBlock = lazy(() => import("../snippets/MarkdownBlock"));

const PULSE_MS = 10000;
const DISARM_MS = 5000;

/** The second click of a double-click lands on whatever replaced the
 * button under it: let it act only on a deliberate click (keyboard
 * activation is detail 0). */
const deliberate = (event) => !(event.detail > 1);
const holdFocusOnDoubleClick = (event) => {
  if (event.detail > 1) event.preventDefault();
};

/** Move focus once the change it follows is on screen. Whenever an action
 * swaps out the control you were on, focus moves on purpose instead of
 * falling to <body>. */
function useFocusAfterRender() {
  const [request, setRequest] = useState(null);
  useEffect(() => {
    request?.find()?.focus();
  }, [request]);
  return useCallback((find) => setRequest({ find }), []);
}

function Author({ post }) {
  const { author, author_owner: owner } = post;
  if (!author) {
    return <span className="text-muted-foreground">A deleted account</span>;
  }
  return (
    <span className="flex flex-wrap items-center gap-2">
      <UserAvatar user={author} size="sm" decorative />
      <strong>{displayName(author)}</strong>
      {author.kind === "agent" && (
        <>
          {" "}
          <Badge variant="outline" className="font-normal">
            {owner ? `agent · ${displayName(owner)}'s` : "agent"}
          </Badge>
        </>
      )}
    </span>
  );
}

Author.propTypes = {
  post: PropTypes.object.isRequired,
};

function PostItem({
  post,
  isOpeningPost,
  answersClosed,
  taskUnlinked,
  onMakeTask,
  onDelete,
}) {
  const who = post.author ? displayName(post.author) : "a deleted account";
  const when = relativeTime(post.created_at);
  // A closed answer can't become a task (the API refuses it too): the task
  // would carry it past the panel's blindness. Nor can a post whose task
  // was made but not linked — "Link it again" is the way, not a second one.
  const canMakeTask =
    !post.hidden &&
    !post.task &&
    !taskUnlinked &&
    !(post.is_answer && answersClosed);
  const canDelete = post.can_delete && !isOpeningPost;
  return (
    <li
      data-post-id={post.id}
      className={`rounded-lg border bg-card p-4 text-card-foreground${post.is_answer ? " border-l-4 border-l-primary" : ""}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <Author post={post} />
        {post.is_answer && (
          <>
            {" "}
            <Badge variant="secondary" className="font-normal">
              Answer
            </Badge>
          </>
        )}{" "}
        <time
          className="text-muted-foreground"
          dateTime={post.created_at}
          title={post.created_at}
        >
          {when}
        </time>
      </div>
      {post.hidden ? (
        <p className="m-0 text-sm italic text-muted-foreground">
          Answered — hidden from you until the answers open.
        </p>
      ) : (
        <Suspense
          fallback={
            <pre className="m-0 font-sans whitespace-pre-wrap break-words">
              {post.body}
            </pre>
          }
        >
          <MarkdownBlock body={post.body || ""} />
        </Suspense>
      )}
      {(post.task || canMakeTask || canDelete) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          {post.task && (
            <Link
              to={`/tasks/board?task=${post.task.id}`}
              data-task-link={post.id}
              className="text-primary underline-offset-4 hover:underline"
            >
              → Task: {post.task.title}
            </Link>
          )}
          {canMakeTask && (
            <Button
              size="sm"
              variant="outline"
              aria-label={`Make this a task — ${who}'s post from ${when}`}
              onClick={() => onMakeTask(post)}
            >
              Make this a task
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              className="text-destructive hover:text-destructive"
              aria-label={`Delete ${who}'s post from ${when}`}
              onClick={() => onDelete(post)}
            >
              Delete
            </Button>
          )}
        </div>
      )}
    </li>
  );
}

PostItem.propTypes = {
  post: PropTypes.object.isRequired,
  isOpeningPost: PropTypes.bool,
  answersClosed: PropTypes.bool,
  taskUnlinked: PropTypes.bool,
  onMakeTask: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function PanelStatus({ discussion, busy, onOpen, sectionRef }) {
  const { answers_open: open, panelists } = discussion;
  const [armed, setArmed] = useState(false);
  const controlsRef = useRef(null);
  const openRef = useRef(null);
  const cancelRef = useRef(null);
  const refocus = useRef(null); // "cancel" | "open" | null

  const arm = () => {
    refocus.current = "cancel"; // the safe one: a second Enter cancels
    setArmed(true);
  };
  const disarm = () => {
    refocus.current = controlsRef.current?.contains(document.activeElement)
      ? "open"
      : null;
    setArmed(false);
  };

  useEffect(() => {
    const target = { cancel: cancelRef, open: openRef }[refocus.current];
    refocus.current = null;
    target?.current?.focus();
  }, [armed]);

  // An armed "open them now" disarms itself: a confirm left sitting there
  // shouldn't fire an hour later. It holds while the request runs.
  useEffect(() => {
    if (!armed || busy) return undefined;
    const t = setTimeout(disarm, DISARM_MS);
    return () => clearTimeout(t);
  }, [armed, busy]);

  return (
    <section
      ref={sectionRef}
      tabIndex={-1}
      aria-label="Panel"
      className="mb-4 rounded-lg border bg-muted/40 p-4 text-sm"
    >
      <p className="m-0 mb-2">
        <strong>Panel</strong> · {panelSummary(discussion)}.{" "}
        {open
          ? "Everyone can read every answer now."
          : "The panelists can't see each other's answers until everyone has answered."}
      </p>
      <ul
        className="m-0 flex list-none flex-wrap gap-2 p-0"
        aria-label="Panelists"
      >
        {panelists.map(({ user, answered }) => (
          <li
            key={user.id}
            className="flex items-center gap-1.5 rounded-full border bg-card px-2 py-0.5"
          >
            <UserAvatar user={user} size="xs" decorative />
            {displayName(user)}{" "}
            {answered ? (
              <span className="flex items-center text-success">
                <Check className="size-3.5" aria-hidden="true" />
                <span className="sr-only">answered</span>
              </span>
            ) : (
              <span className="text-muted-foreground">(waiting)</span>
            )}
          </li>
        ))}
      </ul>
      {discussion.can_open_answers && (
        <div
          ref={controlsRef}
          className="mt-3 flex flex-wrap items-center gap-3"
        >
          {armed ? (
            <>
              {/* Lands where "Open the answers now" was: a double-click's
                  second click must not confirm. */}
              <Button
                size="sm"
                variant="destructive"
                disabled={busy}
                onMouseDown={holdFocusOnDoubleClick}
                onClick={(e) => {
                  if (deliberate(e)) onOpen();
                }}
              >
                {busy && <Loader2 className="animate-spin" />}
                Yes, open them now
              </Button>
              <Button
                ref={cancelRef}
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={disarm}
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              ref={openRef}
              size="sm"
              variant="outline"
              onClick={(e) => {
                if (deliberate(e)) arm();
              }}
            >
              Open the answers now
            </Button>
          )}
          <span className="text-muted-foreground">
            Panelists who haven&apos;t answered will see the others first.
          </span>
        </div>
      )}
    </section>
  );
}

PanelStatus.propTypes = {
  discussion: PropTypes.object.isRequired,
  busy: PropTypes.bool,
  onOpen: PropTypes.func.isRequired,
  sectionRef: PropTypes.object,
};

function DecisionBox({ discussion, onDecide }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const focusAfterRender = useFocusAfterRender();
  const {
    decision,
    decided_by: by,
    decided_at: at,
    can_decide: canDecide,
  } = discussion;

  const edit = (text) => {
    setDraft(text);
    setEditing(true);
    focusAfterRender(() => inputRef.current);
  };

  const close = () => {
    setEditing(false);
    // The editor's controls are gone: land on the decision (or its button).
    focusAfterRender(() => boxRef.current);
  };

  const save = async (text) => {
    setSaving(true);
    setError(null);
    try {
      await onDecide(text);
      close();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save the decision."));
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <form
        className="mb-4 grid gap-2 rounded-lg border bg-card p-4"
        onSubmit={(e) => {
          e.preventDefault();
          save(draft);
        }}
      >
        <Label htmlFor="decision">Decision</Label>
        <Input
          id="decision"
          ref={inputRef}
          value={draft}
          maxLength={500}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What was decided, in a line"
        />
        {error && <p className="m-0 text-sm text-destructive">{error}</p>}
        <div className="flex gap-2">
          {/* "Change" was under the cursor: a double-click's second click
              lands here, so only a deliberate click saves. */}
          <Button
            size="sm"
            type="submit"
            disabled={saving || !draft.trim()}
            onMouseDown={holdFocusOnDoubleClick}
            onClick={(e) => {
              if (!deliberate(e)) e.preventDefault();
            }}
          >
            Save decision
          </Button>
          <Button size="sm" type="button" variant="outline" onClick={close}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  if (decision) {
    return (
      <section
        ref={boxRef}
        tabIndex={-1}
        aria-label="Decision"
        className="mb-4 rounded-lg border border-l-4 border-l-success bg-card p-4"
      >
        <p className="m-0 text-sm font-medium text-muted-foreground">Decided</p>
        <p className="m-0 font-medium">{decision}</p>
        <p className="m-0 text-sm text-muted-foreground">
          {by ? displayName(by) : "Someone"} · {relativeTime(at)}
        </p>
        {error && <p className="m-0 text-sm text-destructive">{error}</p>}
        {canDecide && (
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="outline" onClick={() => edit(decision)}>
              Change
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => save("")}
              disabled={saving}
            >
              Clear
            </Button>
          </div>
        )}
      </section>
    );
  }

  if (!canDecide) return null;
  return (
    <div className="mb-4">
      <Button
        ref={boxRef}
        size="sm"
        variant="ghost"
        onClick={() => edit("")}
      >
        Record a decision
      </Button>
    </div>
  );
}

DecisionBox.propTypes = {
  discussion: PropTypes.object.isRequired,
  onDecide: PropTypes.func.isRequired,
};

/** The board's task dialog, mounted only while it's open — the projects
 * it needs load then, not on every visit to a thread. */
function TaskDialog({
  post,
  discussion,
  users,
  me,
  onSave,
  onClose,
  onCloseAutoFocus,
}) {
  const { projects } = useProjects();
  return (
    <TaskModal
      show
      onHide={onClose}
      onCloseAutoFocus={onCloseAutoFocus}
      projects={projects}
      users={users}
      me={me?.id ?? null}
      defaultTitle={taskTitleFrom(post.body)}
      defaultDescription={taskDescriptionFrom(post.body, discussion)}
      onSave={onSave}
      onDelete={() => {}}
    />
  );
}

TaskDialog.propTypes = {
  post: PropTypes.object.isRequired,
  discussion: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  me: PropTypes.object,
  onSave: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onCloseAutoFocus: PropTypes.func,
};

/**
 * One discussion (projects/discussions D3, D8–D10, D13). Panels keep each
 * panelist's answer from the others until everyone has answered; the page
 * says so wherever it changes what you can do.
 *
 * Freshness: the thread arrives with its pulse stamp, and only a thread
 * that arrived moves it — so a change is fetched until a fetch succeeds,
 * and none of our own actions can hide someone else's (review of #384).
 */
function DiscussionThread() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useOutletContext() || {};
  const { users } = useHouseholdUsers();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [notice, setNotice] = useState(null); // {postId, text, retry}
  const [news, setNews] = useState({ n: 0, text: "" });
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [openingAnswers, setOpeningAnswers] = useState(false);
  const [confirm, setConfirm] = useState(null); // {post} | {discussion: true}
  const [deleting, setDeleting] = useState(false);
  const [taskFor, setTaskFor] = useState(null);
  const focusAfterRender = useFocusAfterRender();
  const composerRef = useRef(null);
  const lockedNoteRef = useRef(null);
  const panelRef = useRef(null);
  const postsRef = useRef(null);
  const retryLinkRef = useRef(null);
  const stamp = useRef(null);
  const shown = useRef(null); // the thread on screen, to tell what's new
  const meId = useRef(null);
  meId.current = me?.id ?? null;

  // Only the newest thread lands: reloads AND our own actions bump this,
  // so an older GET can never overwrite a newer answer.
  const request = useRef(0);

  // When an action behind a dialog moves focus on purpose (the posts
  // after a delete, the new task's link), the closing dialog must not hand
  // it back to its opener a frame later — in Safari, or after a scripted
  // click, the opener is whatever had focus before, not the button.
  const focusMoved = useRef(false);
  const keepMovedFocus = (event) => {
    if (focusMoved.current) event.preventDefault();
    focusMoved.current = false;
  };

  /** Put a thread on screen: its stamp, what's new, "read up to here". */
  const show = useCallback(
    (data) => {
      const before = shown.current;
      if (before) {
        const known = new Set(before.posts.map((p) => p.id));
        const fresh = data.posts.filter(
          (p) => !known.has(p.id) && p.author?.id !== meId.current,
        );
        if (fresh.length) {
          setNews(({ n }) => ({
            n: n + 1,
            text:
              fresh.length === 1
                ? `New post from ${fresh[0].author ? displayName(fresh[0].author) : "a deleted account"}`
                : `${fresh.length} new posts`,
          }));
        }
      }
      shown.current = data;
      stamp.current = data.stamp;
      setD(data);
      setLoadError(null);
      // Read up to what this response showed — not "now", which would
      // swallow a post that landed after this fetch. The server says how
      // far that is: its newest post, or when the answers opened if later
      // (an early opening comes after every post; D5, review of #386).
      // Best-effort: if it fails, the thread just stays "new" in the list.
      // Read → the nav's "for you" count may have dropped (D14).
      discussionApi
        .read(id, data.read_through)
        .then(discussionsChanged)
        .catch(() => {});
    },
    [id],
  );

  const load = useCallback(
    async ({ quiet = false, strict = false } = {}) => {
      const rid = ++request.current;
      if (!quiet) setLoading(true);
      try {
        const { data } = await discussionApi.get(id);
        if (rid !== request.current) return;
        show(data);
      } catch (err) {
        if (rid !== request.current) return;
        if (err?.response?.status === 404) setLoadError("notfound");
        else if (!quiet) setLoadError("load");
        if (strict) throw err;
      } finally {
        if (rid === request.current && !quiet) setLoading(false);
      }
    },
    [id, show],
  );

  // An action's own answer is the newest thread there is: a GET still in
  // flight is older, and must not land on top of it.
  const apply = (data) => {
    ++request.current;
    show(data);
  };

  // Another thread (Back/Forward between two): start clean, so a failed
  // load can't leave the last thread on screen under the new URL.
  useEffect(() => {
    stamp.current = null;
    shown.current = null;
    setD(null);
    load();
  }, [load]);

  // D10: agents reply on their own time — new posts appear without a
  // reload. A failed reload throws, so the stamp stays put and the next
  // tick tries again; a thread that's gone says so.
  usePoll(
    async ({ stale }) => {
      let data;
      try {
        ({ data } = await discussionApi.pulse(id));
      } catch (err) {
        if (!stale() && err?.response?.status === 404) {
          setLoadError("notfound");
          return;
        }
        throw err;
      }
      if (stale()) return;
      if (data.stamp !== stamp.current) {
        await load({ quiet: true, strict: true });
      }
    },
    {
      intervalMs: PULSE_MS,
      enabled: Boolean(d) && loadError !== "notfound",
      resetKey: id,
    },
  );

  const post = async (event) => {
    event.preventDefault();
    setPosting(true);
    setActionError(null);
    try {
      await discussionApi.reply(id, draft);
      setDraft("");
      await load({ quiet: true });
      // An answer to a closed panel swaps the composer for a note.
      focusAfterRender(() => lockedNoteRef.current || composerRef.current);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't post that. Try again."));
    } finally {
      setPosting(false);
    }
  };

  const openAnswers = async () => {
    setOpeningAnswers(true);
    setActionError(null);
    try {
      const { data } = await discussionApi.openAnswers(id);
      apply(data);
      focusAfterRender(() => panelRef.current);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't open the answers."));
    } finally {
      setOpeningAnswers(false);
    }
  };

  const decide = async (text) => {
    const { data } = await discussionApi.decide(id, text);
    apply(data);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setActionError(null);
    try {
      if (confirm.discussion) {
        await discussionApi.delete(id);
        // Replace: Back mustn't land on "this discussion isn't here".
        navigate("/discussions", { replace: true });
        return;
      }
      await discussionApi.deletePost(id, confirm.post.id);
      focusMoved.current = true;
      setConfirm(null);
      await load({ quiet: true });
      focusAfterRender(() => postsRef.current);
    } catch (err) {
      setActionError(apiErrorMessage(err, "Couldn't delete it."));
      setConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const link = async (postId, task) => {
    try {
      await discussionApi.linkTask(id, postId, task.id);
      setNotice(null);
      await load({ quiet: true });
      focusAfterRender(() =>
        document.querySelector(`[data-task-link="${postId}"]`),
      );
    } catch {
      setNotice({
        postId,
        text: `The task “${task.title}” was created, but it couldn't be linked to the post.`,
        retry: () => link(postId, task),
      });
      focusAfterRender(() => retryLinkRef.current);
    }
  };

  // "Make this a task": the board's own dialog, prefilled; then the post
  // links to the task. Created but not linked offers "Link it again" —
  // never a second task.
  const saveTask = async (payload) => {
    const source = taskFor;
    const { data: task } = await taskApi.create(payload);
    // Linked or not, focus goes somewhere on purpose (the link, or "Link
    // it again"). A failed create keeps the dialog open: no claim then.
    focusMoved.current = true;
    await link(source.id, task);
  };

  if (loading) {
    return (
      <div className="page-container mt-6 py-12 pt-4 text-center" role="status">
        <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading the discussion…</span>
      </div>
    );
  }

  if (loadError === "notfound" || (loadError && !d)) {
    return (
      <div className="page-container mt-6 pt-4">
        <Alert
          className="mb-3"
          variant={loadError === "notfound" ? "default" : "destructive"}
        >
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              {loadError === "notfound"
                ? "This discussion isn't here — it was deleted, or it's not shared with you."
                : "Couldn't load the discussion."}
            </span>
            {loadError !== "notfound" && (
              <Button size="sm" variant="outline" onClick={() => load()}>
                Retry
              </Button>
            )}
          </AlertDescription>
        </Alert>
        <Link
          to="/discussions"
          className="text-primary underline-offset-4 hover:underline"
        >
          Back to discussions
        </Link>
      </div>
    );
  }

  // An invisible name can't be typed as an @mention (#38), so no chip.
  const others = users.filter(
    (u) => u.id !== me?.id && !isBlankName(u.username),
  );
  const answering = Boolean(d.waiting_on_you);
  const answersClosed = d.is_panel && !d.answers_open;

  return (
    <div className="page-container mt-6 pt-4" style={{ maxWidth: 880 }}>
      {/* New posts from others, said out loud: a fresh node each time, so
          the same words twice are still announced. */}
      <div className="sr-only" role="status" aria-live="polite">
        {news.text && <span key={news.n}>{news.text}</span>}
      </div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title={d.title}
          subtitle={`Started by ${displayName(d.user)} · ${relativeTime(d.created_at)}`}
        />
        {d.can_delete && (
          <Button
            variant="outline"
            className="mb-6 shrink-0 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setConfirm({ discussion: true })}
          >
            Delete discussion
          </Button>
        )}
      </div>

      <DecisionBox discussion={d} onDecide={decide} />

      {d.is_panel && (
        <PanelStatus
          discussion={d}
          busy={openingAnswers}
          onOpen={openAnswers}
          sectionRef={panelRef}
        />
      )}

      {answering && (
        <Alert className="mb-4">
          <AlertDescription>
            You&apos;re on this panel. Your next post is your answer — you
            won&apos;t see the other answers until everyone has answered or
            the asker opens them.
          </AlertDescription>
        </Alert>
      )}

      {notice && (
        <Alert className="mb-4">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{notice.text}</span>
            <Button
              ref={retryLinkRef}
              size="sm"
              variant="outline"
              onClick={notice.retry}
            >
              Link it again
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setNotice(null)}>
              Dismiss
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {actionError && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      )}

      <ol
        ref={postsRef}
        tabIndex={-1}
        className="m-0 mb-6 flex list-none flex-col gap-3 p-0"
        aria-label="Posts"
      >
        {d.posts.map((p, i) => (
          <PostItem
            key={p.id}
            post={p}
            isOpeningPost={i === 0}
            answersClosed={answersClosed}
            taskUnlinked={notice?.postId === p.id}
            onMakeTask={setTaskFor}
            onDelete={(target) => setConfirm({ post: target })}
          />
        ))}
      </ol>

      {/* Whether you can post is the server's call (can_post) — the page
          never guesses it from who it thinks you are. */}
      {!d.can_post ? (
        <p
          ref={lockedNoteRef}
          tabIndex={-1}
          className="mb-8 rounded-lg border bg-muted/40 p-4 text-sm text-muted-foreground"
        >
          You&apos;ve answered. The other answers open when everyone has
          answered, or when the asker opens them — then you can reply.
        </p>
      ) : (
        <form onSubmit={post} className="mb-8 grid gap-2">
          <Label htmlFor="reply">{answering ? "Your answer" : "Reply"}</Label>
          <Textarea
            id="reply"
            ref={composerRef}
            rows={4}
            value={draft}
            maxLength={10000}
            onChange={(e) => setDraft(e.target.value)}
            className="max-h-48"
            aria-describedby="reply-hint"
          />
          <p id="reply-hint" className="m-0 text-sm text-muted-foreground">
            Markdown works. Mention someone with @name — it shows up under “For
            you” for them.
          </p>
          {others.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 text-sm">
              <span className="text-muted-foreground">Mention:</span>
              {others.map((u) => (
                <Button
                  key={u.id}
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 px-2"
                  aria-label={`Mention @${u.username}`}
                  onClick={() => {
                    setDraft((text) => withMention(text, u.username));
                    composerRef.current?.focus();
                  }}
                >
                  @{u.username}
                </Button>
              ))}
            </div>
          )}
          <div>
            <Button
              type="submit"
              className="h-11 md:h-9"
              disabled={posting || !draft.trim()}
            >
              {posting && <Loader2 className="animate-spin" />}
              {answering ? "Post my answer" : "Post"}
            </Button>
          </div>
        </form>
      )}

      <Dialog
        open={Boolean(confirm)}
        onOpenChange={(open) => !open && !deleting && setConfirm(null)}
      >
        <DialogContent
          showCloseButton={!deleting}
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => deleting && event.preventDefault()}
          onCloseAutoFocus={keepMovedFocus}
        >
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {confirm?.discussion
                ? "Delete this discussion?"
                : "Delete this post?"}
            </DialogTitle>
            <DialogDescription>
              {confirm?.discussion
                ? "Every post in it goes too, for everyone. This can't be undone."
                : "It goes for everyone. This can't be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setConfirm(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting && <Loader2 className="animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {taskFor && (
        <TaskDialog
          post={taskFor}
          discussion={d}
          users={users}
          me={me}
          onSave={saveTask}
          onClose={() => setTaskFor(null)}
          onCloseAutoFocus={keepMovedFocus}
        />
      )}
    </div>
  );
}

export default DiscussionThread;
